import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  Stack,
  TextField
} from "@fluentui/react";
import { Controller, useForm, useWatch } from "react-hook-form";

import {
  contractShipmentTypeLabels,
  ContractShipmentType,
  type CreateContractShipmentRequest,
  type UpdateContractShipmentRequest
} from "@/interfaces/modules/contract-shipments";
import { ListContractShipmentsResponseItem } from "@/interfaces/modules/contract-shipments/contract-shipments.api.types";
import { formatCentsToInput, parseReaisToCents } from "@/utils/FormatBRL";

interface ContractShipmentFormDialogProps {
  shipment?: ListContractShipmentsResponseItem;
  canListCosts: boolean;
  isSaving: boolean;
  onDismiss: () => void;
  onSave: (
    data: CreateContractShipmentRequest | UpdateContractShipmentRequest
  ) => Promise<boolean>;
}

interface ShipmentFormValues {
  type: ContractShipmentType;
  code: string;
  cost: string;
  notes: string;
}

const typeOptions: Array<IDropdownOption> = Object.entries(
  contractShipmentTypeLabels
).map(([key, text]) => ({ key, text }));

const initialFormValues: ShipmentFormValues = {
  type: ContractShipmentType.POST_OFFICE,
  code: "",
  cost: "",
  notes: ""
};

function getShipmentFormValues(
  shipment?: ListContractShipmentsResponseItem
): ShipmentFormValues {
  if (!shipment) return initialFormValues;

  return {
    type: shipment.type,
    code: shipment.code,
    cost: "cost" in shipment ? formatCentsToInput(shipment.cost) : "",
    notes: shipment.notes ?? ""
  };
}

export function ContractShipmentFormDialog({
  shipment,
  canListCosts,
  isSaving,
  onDismiss,
  onSave
}: ContractShipmentFormDialogProps): JSX.Element {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValid }
  } = useForm<ShipmentFormValues>({
    defaultValues: getShipmentFormValues(shipment),
    mode: "onChange"
  });
  const shipmentType = useWatch({ control, name: "type" });
  const isDisabled = isSaving || isSubmitting;

  async function handleSave(values: ShipmentFormValues) {
    const data: CreateContractShipmentRequest | UpdateContractShipmentRequest =
      {
        type: values.type,
        code: values.code.trim(),
        notes: values.notes.trim()
      };

    if (canListCosts) {
      const cost =
        values.type === ContractShipmentType.POUCH || values.cost.trim() === ""
          ? 0
          : parseReaisToCents(values.cost);

      if (cost === null) return;

      data.cost = cost;
    }

    const wasSaved = await onSave(data);

    if (wasSaved) onDismiss();
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: shipment ? "Editar envio" : "Adicionar envio",
        closeButtonAriaLabel: "Fechar"
      }}
      modalProps={{ isBlocking: true }}
      minWidth={460}
    >
      <form onSubmit={event => void handleSubmit(handleSave)(event)}>
        <Stack tokens={{ childrenGap: 12 }}>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Dropdown
                label="Tipo de envio"
                required
                disabled={isDisabled}
                selectedKey={field.value}
                options={typeOptions}
                onBlur={field.onBlur}
                onChange={(_, option) => {
                  if (!option) return;

                  const type = option.key as ContractShipmentType;
                  field.onChange(type);

                  if (canListCosts && type === ContractShipmentType.POUCH) {
                    setValue("cost", formatCentsToInput(0), {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  }
                }}
              />
            )}
          />

          <Controller
            name="code"
            control={control}
            rules={{
              validate: value =>
                value.trim().length > 0 || "Informe o código do envio."
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={
                  shipmentType === ContractShipmentType.POUCH
                    ? "Código do malote"
                    : "Código de rastreio"
                }
                required
                disabled={isDisabled}
                value={field.value}
                onBlur={field.onBlur}
                onChange={(_, value) => field.onChange(value ?? "")}
                errorMessage={fieldState.error?.message}
              />
            )}
          />

          {canListCosts && (
            <Controller
              name="cost"
              control={control}
              rules={{
                deps: ["type"],
                validate: (value, values) => {
                  if (
                    values.type === ContractShipmentType.POUCH ||
                    value.trim() === ""
                  )
                    return true;

                  return (
                    parseReaisToCents(value) !== null ||
                    "Informe um custo válido."
                  );
                }
              }}
              render={({ field, fieldState }) => (
                <TextField
                  label="Custo do envio"
                  description={
                    shipmentType === ContractShipmentType.POST_OFFICE
                      ? "Opcional. Pode ser informado após a postagem."
                      : undefined
                  }
                  prefix="R$"
                  type="text"
                  inputMode="decimal"
                  disabled={
                    isDisabled || shipmentType === ContractShipmentType.POUCH
                  }
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(_, value) => field.onChange(value ?? "")}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          )}

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                label="Anotações"
                multiline
                rows={3}
                disabled={isDisabled}
                value={field.value}
                onBlur={field.onBlur}
                onChange={(_, value) => field.onChange(value ?? "")}
              />
            )}
          />
        </Stack>

        <DialogFooter>
          <DefaultButton
            text="Cancelar"
            type="button"
            onClick={onDismiss}
            disabled={isDisabled}
          />
          <PrimaryButton
            text="Salvar"
            type="submit"
            disabled={!isValid || isDisabled}
          />
        </DialogFooter>
      </form>
    </Dialog>
  );
}
